import { useMemo } from 'react';
import { LineChart, LineChartProps } from '@mui/x-charts/LineChart';
import { formatMoney, SupplierCreditApplicationMonthSummary } from '@dill/dill-shared';
import { COLORS } from '../../../../../utils/colors';

interface CreditAmountsChartProps {
  selectedYear: string;
  monthlyData: SupplierCreditApplicationMonthSummary[];
}

const getResponsiveConfig = (): Partial<LineChartProps> => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return {
    height: isMobile ? 350 : 400,
    margin: {
      left: isMobile ? 80 : 100,
      right: isMobile ? 80 : 100,
      top: 20,
      bottom: isMobile ? 80 : 60,
    },
  };
};

function CreditAmountsChart({ selectedYear, monthlyData }: CreditAmountsChartProps) {
  const chartData = useMemo(() => {
    return monthlyData.length > 0
      ? [...monthlyData]
          .sort((a, b) => parseInt(a.month) - parseInt(b.month))
          .map((data) => ({
            month: data.monthName.substring(0, 3), // Convert "January" to "Jan"
            totalCreditsApproved: data.totalCreditApproved,
            averageCreditSize: data.averageCreditApproved,
          }))
      : [];
  }, [monthlyData]);

  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
            Credit Amounts Analysis
          </h3>
          <p className="text-sm text-gray-500">
            Monthly total credits approved and average credit size for {selectedYear}
          </p>
        </div>
      </div>

      <LineChart
        dataset={chartData}
        xAxis={[
          {
            dataKey: 'month',
            scaleType: 'point',
            tickLabelStyle: { fontSize: 12, fill: COLORS.TEXT_SECONDARY },
          },
        ]}
        series={[
          {
            dataKey: 'totalCreditsApproved',
            label: 'Total Credits Approved',
            color: COLORS.PRIMARY_500,
            curve: 'linear',
            valueFormatter: (value: number | null) => {
              return formatMoney(value || 0, 0);
            },
          },
          {
            dataKey: 'averageCreditSize',
            label: 'Average Credit Size',
            color: COLORS.WARNING_500,
            curve: 'linear',
            valueFormatter: (value: number | null) => {
              return formatMoney(value || 0, 0);
            },
          },
        ]}
        yAxis={[
          {
            width: 90,
            tickLabelStyle: { fontSize: 11, fill: COLORS.TEXT_SECONDARY },
            min: 0,
            valueFormatter: (value: number) => {
              return formatMoney(value, 0);
            },
          },
        ]}
        grid={{ horizontal: true, vertical: false }}
        {...getResponsiveConfig()}
      />
    </div>
  );
}

export default CreditAmountsChart;
